/** @typedef {import('headlight-configurable-application').Pict} Pict */

const MaterialsEntityInputType = require('./PictProvider-InputType-MaterialsEntitySelector.js');
const libLabRouting = require('./_LabRouting.js');

/**
 * MaterialsTestLabSelector — per-row lab selector for the Testing table.
 *
 * SIBLING of MaterialsLabSelector (NOT a subclass): both are thin layers over MaterialsEntitySelector with
 * Entity:'Lab', so the lab UI (select2 / list / display) is shared base behavior. The difference is the BIND
 * TARGET — MaterialsLabSelector binds the sample's labs (SampleLabJoin); this one binds a single TEST's lab
 * (the row's TestInstance), so it stays a sibling off the base to avoid the 3-level super-entanglement on
 * onInputInitialize.
 *
 * On a lab pick it DUAL-WRITES the test's lab — `TestInstance.IDLab` (direct FK on the row's TestInstance) and
 * a `TestInstanceLabJoin` (IDTestInstance→IDLab) — and asks the sample's lab selector to reconcile with the
 * per-test labs (`SampleLabSync`, see _LabRouting). Single-select (one lab per test). Tabular: the row record is the test triple from
 * the Test selector (`{TestInstance, TestInstanceSampleJoin, DocumentPolyJoin}`), TestInstance carrying IDLab.
 */
/**
 * Install the pin-to-top resolver for a lab selector's select2 options, when the descriptor scopes by
 * `LabScope` and the LabScopeResolver is registered. The resolver answers with its preference ranking
 * (`LabScopeResolver.getPreferredLabIDs`); nothing is hidden.
 *
 * @param {import('./PictProvider-Select2Util.js').Select2AjaxOptions} pConfigOptions
 * @param {Object} pInput @param {number} [pRowIndex] @param {Object} [pView]
 */
function applyLabPriorityIDs(pPict, pConfigOptions, pInput, pRowIndex, pView)
{
	const tmpEngine = pPict.providers.EntityFilterEngine;
	const tmpResolver = pPict.providers.LabScopeResolver;
	if (!tmpEngine || !tmpResolver || pConfigOptions.GetPriorityIDs)
	{
		return;
	}
	const tmpFilters = (pInput && pInput.PictForm) ? pInput.PictForm.DynamicFilters : null;
	if (!Array.isArray(tmpFilters))
	{
		return;
	}
	const tmpFilter = tmpFilters.find((pEntry) => pEntry && pEntry.LabScope);
	if (!tmpFilter)
	{
		return;
	}
	const tmpRowIndex = (pRowIndex === undefined) ? null : pRowIndex;
	pConfigOptions.GetPriorityIDs = () =>
	{
		return tmpResolver.getPreferredLabIDs(tmpFilter.LabScope,
			{
				IDTest: tmpEngine._readLabScopeTestID(tmpFilter, pInput, tmpRowIndex, pView || null),
				SelectedLabIDs: libLabRouting.collectSelectedTestLabIDs(pPict),
			});
	};
}

class MaterialsTestLabSelectorInputType extends MaterialsEntityInputType
{
	constructor(pFable, pOptions, pServiceHash)
	{
		if (!pOptions)
		{
			pOptions = { };
		}
		pOptions.Entity = 'Lab';
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * @param {Object} pView @param {Object} pGroup @param {Object} pRow @param {Object} pInput
	 * @param {any} pValue @param {string} pHTMLSelector @param {string} pTransactionGUID @return {boolean}
	 */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._applyTestLabDefaults(pInput);
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * @param {Object} pView @param {Object} pGroup @param {Object} pInput @param {any} pValue
	 * @param {string} pHTMLSelector @param {number} pRowIndex @param {string} pTransactionGUID @return {any}
	 */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._applyTestLabDefaults(pInput);
		const tmpResult = super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
		// The col binds to the nested TestInstance.IDLab, which a tabular cell does NOT marshal into pValue on
		// (re)open — so a persisted/adopted lab renders blank. Passing the id to super only reaches the fetch; it is
		// never written to the cell's storage, so it's lost on re-marshal. PUSH it via setData — which writes the
		// value AND drives the fetch + select2 paint, exactly like a fresh pick.
		if (libLabRouting.isUnsetLabID(pValue) && pRowIndex != null)
		{
			const tmpTestInstance = this._getRowTestInstance(pView, pInput, pRowIndex);
			if (tmpTestInstance && tmpTestInstance.IDLab)
			{
				pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, tmpTestInstance.IDLab);
			}
			else
			{
				this._autoFillTestLab(pView, pInput, pRowIndex);
			}
		}
		return tmpResult;
	}

	/**
	 * Fill a row with the resolver's auto-fill answer (see LabScopeResolver.getAutoFillLabID). Only ever runs on a
	 * row that has no lab, so a persisted or user-chosen value is never overwritten; committed through the same
	 * setData path a user pick uses, so the join dual-write and the sample-lab sync ride along unchanged.
	 * @param {Object} pView @param {Object} pInput @param {number|string} pRowIndex
	 */
	_autoFillTestLab(pView, pInput, pRowIndex)
	{
		libLabRouting.autoFillTestLab(this.pict, pInput, pRowIndex, pView,
			() => this._getRowTestInstance(pView, pInput, pRowIndex)?.IDLab,
			(pIDLab) => pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, pIDLab));
	}

	/**
	 * One lab per test (single-select), tag on Name.
	 * @param {Object} pInput
	 */
	_applyTestLabDefaults(pInput)
	{
		if (!pInput.PictForm)
		{
			pInput.PictForm = { };
		}
		if (pInput.PictForm.Multiple == null)
		{
			pInput.PictForm.Multiple = false;
		}
		if (pInput.PictForm.EntityTag == null)
		{
			pInput.PictForm.EntityTag = 'Name';
		}
	}

	/**
	 * Lab list is customer-scoped (row-level auth), sorted by Name — same as the sample lab selector.
	 * @param {import('./PictProvider-Select2Util.js').Select2AjaxOptions} pConfigOptions
	 * @param {Object} pView @param {Object} pInput @param {number} [pRowIndex]
	 */
	_updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex)
	{
		super._updateSelect2Options(pConfigOptions, pView, pInput, pRowIndex);
		pConfigOptions.SearchFields = pConfigOptions.SearchFields || [ 'Name' ];
		pConfigOptions.HandleSearchFilters = (pFilters) =>
		{
			pFilters.push(`FSF~Name~ASC~0`);
			return pFilters;
		};
		applyLabPriorityIDs(this.pict, pConfigOptions, pInput, pRowIndex, pView);
	}

	/**
	 * On a lab pick: dual-write the test's lab (TestInstance.IDLab + a TestInstanceLabJoin), then ask the sample's
	 * labs to reconcile with the per-test labs. The row record IS the test triple (TestInstance + joins).
	 * @param {Object} pView @param {Object} pInput @param {Object} pRecord @param {string} pRecordAddress
	 * @param {string} pTransactionGUID @param {number} [pRowIndex]
	 */
	_onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex)
	{
		const tmpLab = Array.isArray(pRecord) ? pRecord[0] : pRecord;
		const tmpIDLab = tmpLab && tmpLab.IDLab;
		const tmpTestInstance = this._getRowTestInstance(pView, pInput, pRowIndex);
		if (tmpTestInstance && tmpIDLab)
		{
			tmpTestInstance.IDLab = tmpIDLab;
			this._ensureTestInstanceLabJoin(pView, pInput, pRowIndex, tmpTestInstance, tmpLab);
		}
		libLabRouting.requestSampleLabSync(this.pict);
		const tmpLabAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpLabAddress, pRecord || { });
		pView.marshalToView();
		super._onRecordRefresh(pView, pInput, pRecord, pRecordAddress, pTransactionGUID, pRowIndex);
	}

	/**
	 * The TestInstance for this row. The Testing-G2 table binds to the Test selector's triple collection
	 * (`${TestAddress}_Instances`), so the row record is `{ TestInstance, TestInstanceSampleJoin, ... }`.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @return {Record<string, any>|null}
	 */
	_getRowTestInstance(pView, pInput, pRowIndex)
	{
		const tmpRowAddress = this._getRecordAddress(pView, pInput, pRowIndex);
		// the lab address sits inside the row record; walk up to the row triple and read its TestInstance
		const tmpTripleAddress = pInput.PictForm.TestInstanceTripleAddress || tmpRowAddress.replace(/\.[^.]+$/, '');
		const tmpTriple = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpTripleAddress);
		if (tmpTriple && tmpTriple.TestInstance && typeof tmpTriple.TestInstance === 'object')
		{
			return tmpTriple.TestInstance;
		}
		// fallback: the row record itself is/holds the TestInstance
		return (tmpTriple && typeof tmpTriple === 'object' && !Array.isArray(tmpTriple)) ? tmpTriple : null;
	}

	/**
	 * Create (or retarget) the TestInstanceLabJoin for this test instance, stored on the row triple at
	 * `_TestInstanceLabJoin` so the projection can sweep it into _EntityData.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {Record<string, any>} pTestInstance @param {Record<string, any>} pLab
	 */
	_ensureTestInstanceLabJoin(pView, pInput, pRowIndex, pTestInstance, pLab)
	{
		// Compose the row-triple sibling address (…_Instances[row]._TestInstanceLabJoin) directly. The record
		// address is the lab's own (…row.TestInstance.IDLab__Record), so stripping one segment lands INSIDE the
		// TestInstance — which drags the join into the TestInstance insert and breaks it with malformed SQL.
		const tmpJoinAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, '_TestInstanceLabJoin');
		const tmpExisting = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpJoinAddress);
		const tmpJoin = (tmpExisting && typeof tmpExisting === 'object' && !Array.isArray(tmpExisting)) ? tmpExisting : { };
		tmpJoin.IDLab = pLab.IDLab;
		if (pTestInstance.IDTestInstance > 0)
		{
			tmpJoin.IDTestInstance = pTestInstance.IDTestInstance;
			delete tmpJoin.GUIDTestInstance;
		}
		else if (pTestInstance.GUIDTestInstance)
		{
			tmpJoin.GUIDTestInstance = pTestInstance.GUIDTestInstance;
			delete tmpJoin.IDTestInstance;
		}
		if (!tmpJoin.GUIDTestInstanceLabJoin)
		{
			const tmpDocumentGUID = this.pict.manifest.getValueAtAddress(this.pict, 'AppData.DocumentData.ReportData.GUIDDocument') || '';
			// Keyed off the test instance rather than a fresh UUID. There is one join per instance, so the
			// key has to be re-derivable - a re-mint that cannot reproduce it inserts a duplicate row
			// instead of upserting the existing one.
			let tmpTestKey = `ID${pTestInstance.IDTestInstance}`;
			if (pTestInstance.GUIDTestInstance)
			{
				tmpTestKey = String(pTestInstance.GUIDTestInstance).replace(`${tmpDocumentGUID}-`, '');
			}
			tmpJoin.GUIDTestInstanceLabJoin = `${tmpDocumentGUID}-TILJ-${tmpTestKey}`;
		}
		this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpJoinAddress, tmpJoin);
	}

	/**
	 * Per-row save-time projection hook. The base dispatches this per row on the ProjectEntityDataRecords sweep
	 * (via onAfterEventTabularCompletion). A lab picker creates no Lab reference record, so we override the base
	 * (skipping its AllowEntityCreation-gated Lab upsert) and instead project this row's TestInstanceLabJoin into
	 * the shared _EntityData. Rows with no lab picked staged no join, so the sync no-ops on them.
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {string} pEvent @param {string} pTransactionGUID
	 */
	_handleBaseSaveProjectionEvent(pView, pInput, pRowIndex, pEvent, pTransactionGUID)
	{
		if (typeof pEvent !== 'string')
		{
			return;
		}
		const tmpTarget = this._getProjectionTarget(pInput, pEvent);
		if (!tmpTarget)
		{
			return;
		}
		this._syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, tmpTarget);
	}

	/**
	 * Save-time projection: sweep this row's TestInstanceLabJoin into _EntityData. (TestInstance.IDLab itself
	 * rides the Test selector's TestInstance projection — we only own the join here.)
	 * @param {Object} pView @param {Object} pInput @param {number|string|null} pRowIndex
	 * @param {string} pTransactionGUID @param {Object} pTargetObject
	 */
	_syncRecordsToEntityData(pView, pInput, pRowIndex, pTransactionGUID, pTargetObject)
	{
		const tmpEntityData = pTargetObject;
		if (!tmpEntityData || typeof tmpEntityData !== 'object' || Array.isArray(tmpEntityData))
		{
			return;
		}
		// Compose the row-triple sibling address (…_Instances[row]._TestInstanceLabJoin) directly. The record
		// address is the lab's own (…row.TestInstance.IDLab__Record), so stripping one segment lands INSIDE the
		// TestInstance — which drags the join into the TestInstance insert and breaks it with malformed SQL.
		const tmpJoinAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, '_TestInstanceLabJoin');
		const tmpJoin = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpJoinAddress);
		if (tmpJoin && typeof tmpJoin === 'object' && !Array.isArray(tmpJoin) && tmpJoin.GUIDTestInstanceLabJoin)
		{
			if (!tmpEntityData.TestInstanceLabJoin || typeof tmpEntityData.TestInstanceLabJoin !== 'object' || Array.isArray(tmpEntityData.TestInstanceLabJoin))
			{
				tmpEntityData.TestInstanceLabJoin = { };
			}
			tmpEntityData.TestInstanceLabJoin[tmpJoin.GUIDTestInstanceLabJoin] = tmpJoin;
		}
	}
}

module.exports = MaterialsTestLabSelectorInputType;
module.exports.default_configuration = { };
